/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer, useMemo, useCallback } from "react"
import { useOutletContext, useParams, Link } from "react-router-dom"
import { isAxiosError } from "axios"
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError"
import { Plus, List, Pin, Eye, Pencil } from "lucide-react"
import { useAuth } from "@/services/userService"
import { useGetForum, useGetForumTopicsSWR } from "@/services/forumService"
import { BarLoader } from "react-spinners";
import ForumHeader from "@/components/ForumHeader"
import ErrorMessage from "@/components/ErrorMessage"

/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/fr"

dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)



const initialState = {
    forum: null,
    errorMessage: "",
    isLoading: false,

    isForumLoading: false,
}



const ACTIONS = {
    SET_FORUM: "setForum",
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",

    SET_IS_FORUM_LOADING: "setIsForumLoading",
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_FORUM:
            return { ...state, forum: action.payload }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, errorMessage: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.SET_IS_FORUM_LOADING:
            return { ...state, isForumLoading: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Index() {

    const {forum_id} = useParams()
    const { auth } = useAuth()
    const getForum = useGetForum()
    const {data: topics, isLoading: isLoadingTopics, error: errorTopics} = useGetForumTopicsSWR(forum_id)

    const [ , setPageTitle,  , , , setShowTitle] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)




    /* On supprime le titre */
    useEffect(() => {
        setPageTitle("Liste des sujets")
        setShowTitle(false)

        return () => {
            setPageTitle("Titre")
            setShowTitle(true)
        }
    }, [setPageTitle, setShowTitle])


    /* On gere un etat global de chargement */
    useEffect(() => {
        dispatch({type: ACTIONS.SET_IS_LOADING, payload: isLoadingTopics || state.isForumLoading})
    }, [isLoadingTopics, state.isForumLoading])



    /* Determine si on a le droit de créer un topic */
    const hasTopicCreateRight = useMemo(() => {
        let result = false

        state?.forum?.rights?.map((right) => {
            if(right.right == "topic_create" || right.right == "forum_moderate") {
                result = true
            }
        })
        return result

    }, [state?.forum])



    /* Détermine si pour un sujet donné, on a le droit de l'éditer */
    const hasTopicEditRight = useCallback((topic) => {
        
        if (topic.competition?.active && topic.author.username == auth?.user?.username )
            return true

        let result = false

        state?.forum?.rights?.map((right) => {
            if(right.right == "forum_moderate") {
                result = true
            }
        })

        return result

    }, [state?.forum, auth])



    /* Récupération des informations du forum */
    useEffect(() => {
        const localGetForum = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const forum = await getForum(forum_id)
                dispatch({ type: ACTIONS.SET_FORUM, payload: forum })
            } catch (error) {
                console.error('forums/[forum_id]::getForum::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }

        localGetForum()
    }, [forum_id])



    /* Gestion des erreurs topics */
    useEffect(() => {
        if (errorTopics) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: errorTopics.message })
        }
    }, [errorTopics])




    return (
        <>
            <ErrorMessage errorMessage={state?.errorMessage} />
            <ForumHeader forum={state?.forum} />
            
            <div className="flex-row flex mb-5 justify-end items-center">
                { state.isLoading && <BarLoader color="#696969" size={20} /> }    

                { hasTopicCreateRight && (
                    <Link to={`/forums/${forum_id}/post`} className="flex flex-0 gap-2 items-center border border-gray-500 rounded-sm px-2 py-1 text-base hover:bg-gray-200" lang="fr">
                        <Plus size={24} className="text-primary" />
                        Créer un sujet
                    </Link>
                )}
            </div>
            
            { forum_id && topics?.length > 0 ? (
                <div className="w-full  flex flex-col gap-2 p-3">
                    <table className="w-full  gap-2 p-3 mb-8">
                        <thead>
                            <tr className="h-10 border-b border-gray-300 text-secondary text-sm">
                                <th className="text-left pl-3 "><div className="flex flex-row items-center gap-2"><List size={16} /> Sujets</div></th>
                                <th className="text-center w-1 px-5" lang="fr">Réponses</th>
                                <th className="text-center w-1 px-5" lang="fr">Vues</th>
                                <th className="text-center w-1 px-5" lang="fr">Activité</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topics?.filter((topic) => topic.pinned && topic.competition?.active).map((topic, i) => {
                                return (
                                    <tr key={i} className="h-16 border-b text-secondary hover:bg-gray-50">
                                        <td className="text-left text-primary pl-3">
                                            <div className="flex flex-col gap-1">
                                                <Link to={`/forums/${forum_id}/topics/${topic.id}`} className="text-primary flex flex-row items-center gap-2">
                                                    {topic.status == 'draft' && <span className="text-xs bg-gray-200 p-1 rounded-lg text-secondary" lang="fr">brouillon</span>}
                                                    <Pin size={16} className="text-primary" />
                                                    {topic.no_response && <Eye size={16} className="text-primary" />}
                                                    {topic.title}
                                                </Link>
                                                <span className="text-xs text-gray-400 flex flex-row items-center gap-2" lang="fr">
                                                    Par {topic.author.first_name} {topic.author.last_name}, le {dayjs(topic.creation_date).format('L')}
                                                    { hasTopicEditRight(topic) && <Link className="flex flex-row items-center hover:text-blue-500 cursor-pointer gap-1 text-gray-400"
                                                        to={`/forums/${forum_id}/topics/${topic.id}/edit`}
                                                        lang="fr"
                                                    >
                                                        <Pencil size={12} className="" />
                                                        Modifier
                                                    </Link> }
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-center text-sm">{topic?.messages_count || 0}</td>
                                        <td className="text-center text-sm">{topic?.views_count || 0}</td>
                                        <td className="text-center text-nowrap text-sm" title={dayjs(topic.last_update_date).format('lll')}>{dayjs(topic.last_update_date).fromNow()}</td>
                                    </tr>
                                )
                            })}
                            {topics?.filter((topic) => !topic.pinned && topic.competition?.active).map((topic, i) => {
                                return (
                                    <tr key={i} className="h-16 border-b text-secondary hover:bg-gray-50">
                                        <td className="text-left text-primary pl-3">
                                            <div className="flex flex-col gap-1">
                                                <Link to={`/forums/${forum_id}/topics/${topic.id}`} className="text-primary flex flex-row items-center gap-2">
                                                    {topic.status == 'draft' && <span className="text-xs bg-gray-200 p-1 rounded-lg text-secondary" lang="fr">brouillon</span>}
                                                    {topic.no_response && <Eye size={16} className="text-primary" />}
                                                    {topic.title}
                                                </Link>
                                                <span className="text-xs text-gray-400 flex flex-row items-center gap-2" lang="fr">
                                                    Par {topic.author.first_name} {topic.author.last_name}, le {dayjs(topic.creation_date).format('L')}
                                                    { hasTopicEditRight(topic) && <Link className="flex flex-row items-center hover:text-blue-500 cursor-pointer gap-1 text-gray-400"
                                                        to={`/forums/${forum_id}/topics/${topic.id}/edit`}
                                                        lang="fr"
                                                    >
                                                        <Pencil size={12} className="" />
                                                        Modifier
                                                    </Link> }
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-center text-sm">{topic?.messages_count || 0}</td>
                                        <td className="text-center text-sm">{topic?.views_count || 0}</td>
                                        <td className="text-center text-nowrap text-sm" title={dayjs(topic.last_update_date).format('lll')}>{dayjs(topic.last_update_date).fromNow()}</td>
                                    </tr>
                                )
                            })}

                            {topics?.filter((topic) => topic.competition?.active).length == 0 && (
                                <tr className="h-10 border-b border-gray-300 text-secondary text-sm">
                                    <td className="text-left text-secondary pl-3">Aucun sujet pour le moment</td>
                                    <td className="text-right"></td>
                                    <td className="text-center text-sm"></td>
                                    <td className="text-center text-sm"></td>
                                    <td className="text-center text-nowrap text-sm"></td>
                                </tr>
                            )}

                        </tbody>
                    </table>
                    
                    { topics?.filter((topic) => !topic.competition?.active).length > 0 && (
                        <table className="w-full  gap-2 p-3">
                            <thead>
                                <tr className="h-10 border-b border-gray-300 text-secondary text-sm">
                                    <th className="text-left pl-3 "><div className="flex flex-row items-center gap-2"><List size={16} /> Sujets archivés (anciennes compétitions)</div></th>
                                    <th className="text-right w-1 px-5">Compétition</th>
                                    <th className="text-center w-1 px-5" lang="fr">Réponses</th>
                                    <th className="text-center w-1 px-5" lang="fr">Vues</th>
                                    <th className="text-center w-1 px-5" lang="fr">Activité</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topics?.filter((topic) => topic.pinned && !topic.competition?.active).map((topic, i) => {
                                    return (
                                        <tr key={i} className="h-16 border-b text-secondary hover:bg-gray-50">
                                            <td className="text-left text-primary pl-3">
                                                <div className="flex flex-col gap-1">
                                                    <Link to={`/forums/${forum_id}/topics/${topic.id}`} className="text-primary flex flex-row items-center gap-2">
                                                        {topic.status == 'draft' && <span className="text-xs bg-gray-200 p-1 rounded-lg text-secondary" lang="fr">brouillon</span>}
                                                        <Pin size={16} className="text-primary" />
                                                        {topic.no_response && <Eye size={16} className="text-primary" />}
                                                        {topic.title}
                                                    </Link>
                                                    <span className="text-xs text-gray-400 flex flex-row items-center gap-2" lang="fr">
                                                        Par {topic.author.first_name} {topic.author.last_name}, le {dayjs(topic.creation_date).format('L')}
                                                        { hasTopicEditRight(topic) && <Link className="flex flex-row items-center hover:text-blue-500 cursor-pointer gap-1 text-gray-400"
                                                            to={`/forums/${forum_id}/topics/${topic.id}/edit`}
                                                            lang="fr"
                                                        >
                                                            <Pencil size={12} className="" />
                                                            Modifier
                                                        </Link> }
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="text-right">{topic?.competition?.name}</td>
                                            <td className="text-center text-sm">{topic?.messages_count || 0}</td>
                                            <td className="text-center text-sm">{topic?.views_count || 0}</td>
                                            <td className="text-center text-nowrap text-sm" title={dayjs(topic.last_update_date).format('lll')}>{dayjs(topic.last_update_date).fromNow()}</td>
                                        </tr>
                                    )
                                })}
                                {topics?.filter((topic) => !topic.pinned && !topic.competition?.active).map((topic, i) => {
                                    return (
                                        <tr key={i} className="h-16 border-b text-secondary hover:bg-gray-50">
                                            <td className="text-left text-primary pl-3">
                                                <div className="flex flex-col gap-1">
                                                    <Link to={`/forums/${forum_id}/topics/${topic.id}`} className="text-primary flex flex-row items-center gap-2">
                                                        {topic.status == 'draft' && <span className="text-xs bg-gray-200 p-1 rounded-lg text-secondary" lang="fr">brouillon</span>}
                                                        {topic.no_response && <Eye size={16} className="text-primary" />}
                                                        {topic.title}
                                                    </Link>
                                                    <span className="text-xs text-gray-400 flex flex-row items-center gap-2" lang="fr">
                                                        Par {topic.author.first_name} {topic.author.last_name}, le {dayjs(topic.creation_date).format('L')}
                                                        { hasTopicEditRight(topic) && <Link className="flex flex-row items-center hover:text-blue-500 cursor-pointer gap-1 text-gray-400"
                                                            to={`/forums/${forum_id}/topics/${topic.id}/edit`}
                                                            lang="fr"
                                                        >
                                                            <Pencil size={12} className="" />
                                                            Modifier
                                                        </Link> }
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="text-right">{topic?.competition?.name}</td>
                                            <td className="text-center text-sm">{topic?.messages_count || 0}</td>
                                            <td className="text-center text-sm">{topic?.views_count || 0}</td>
                                            <td className="text-center text-nowrap text-sm" title={dayjs(topic.last_update_date).format('lll')}>{dayjs(topic.last_update_date).fromNow()}</td>
                                        </tr>
                                    )
                                })}
                                
                            </tbody>
                        </table>
                    )}
                </div>
                ) : topics?.length == 0 ? (
                    <div className="w-full bg-gray-100 flex flex-col gap-2 p-3 text-secondary">Aucun sujet pour le moment</div>
                ) : null
            } 
        </>
    )
}